import React, { type ReactElement, useState } from 'react';

import dynamic from 'next/dynamic';
import { useCookies } from 'react-cookie';

import { useTranslation } from '@framework/hooks';
import { PREFERRED_THEME_MODE_COOKIE } from '@framework/utils/cookies';

import {
    ConfigProvider as StyledSystemConfigProvider,
    type ConfigProviderProps,
    type ThemeMode,
    ThemeProvider
} from 'styled-system';

import noResultsGeneric from '@static/node/images/illustrations/no-results-generic.svg';

const DebugThemeSwitch = dynamic(
    () => import('@components/misc/StyledSystemContainer/DebugThemeSwitch'),
    {
        ssr: false
    }
);

type StyledSystemContainerProps = {
    children: ConfigProviderProps['children'];
};

const illustrations = {
    notFound: noResultsGeneric
};

const StyledSystemContainer = ({ children }: StyledSystemContainerProps): ReactElement => {
    const { t, currentLanguage } = useTranslation();
    const [cookies, setCookie] = useCookies([PREFERRED_THEME_MODE_COOKIE]);
    const [themeMode, setThemeMode] = useState<ThemeMode | undefined>(
        cookies[PREFERRED_THEME_MODE_COOKIE]
    );

    const handleThemeModeChange = (newThemeMode: ThemeMode) => {
        setThemeMode(newThemeMode);
        setCookie(PREFERRED_THEME_MODE_COOKIE, newThemeMode, { path: '/' });
    };

    return (
        <StyledSystemConfigProvider
            t={t}
            currentLanguage={currentLanguage}
            defaultAllowClear
            illustrations={illustrations}
        >
            <ThemeProvider mode={themeMode}>
                {children}
                <DebugThemeSwitch
                    themeMode={themeMode}
                    handleThemeModeChange={handleThemeModeChange}
                />
            </ThemeProvider>
        </StyledSystemConfigProvider>
    );
};

export default StyledSystemContainer;
